import { api } from "./client";

export interface UserAccessItem {
  id: string;
  _id: string;
  email: string;
  name: string;
  role: "client" | "analyst" | "admin" | "super_admin";
  status: "active" | "suspended" | "pending";
  is_active: boolean;
  created_at: string;
  first_access_at?: string | null;
  last_login: string | null;
  industry?: string;
  sector?: string;
  plan: string;
  users_count: number;
  assets_count: number;
  is_protected: boolean;
  permission_overrides?: Record<string, boolean>;
  has_custom_overrides: boolean;
}

export interface AccessKpis {
  total_accounts: number;
  clients_count: number;
  analysts_admins_count: number;
  suspended_pending_count: number;
}

export interface GetUsersResponse {
  items: UserAccessItem[];
  total: number;
  page: number;
  pages: number;
  kpis: AccessKpis;
}

export interface CreateAccessUserPayload {
  email: string;
  name: string;
  password?: string;
  role: "client" | "analyst" | "admin" | "super_admin";
  industry?: string;
  plan?: string;
  status?: "active" | "suspended" | "pending";
  permission_overrides?: Record<string, boolean>;
  send_invite?: boolean;
}

import { getUsers, type GetUsersParams } from "./adminUsers";

export async function fetchUsersAccess(params?: GetUsersParams): Promise<GetUsersResponse> {
  const token = localStorage.getItem("access_token") || undefined;
  const res = await getUsers(token, params);
  return res as GetUsersResponse;
}

export async function createAccessUser(payload: CreateAccessUserPayload) {
  const token = localStorage.getItem("access_token");
  const response = await api.post("/admin/users", payload, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return response.data;
}

export async function updateAccessUserStatus(userId: string, status: "active" | "suspended" | "pending") {
  const token = localStorage.getItem("access_token");
  const response = await api.patch(
    `/admin/users/${userId}/status`,
    { status, is_active: status === "active" },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );
  return response.data;
}

export async function updateAccessUserRole(userId: string, role: string) {
  const token = localStorage.getItem("access_token");
  const response = await api.patch(
    `/admin/users/${userId}/role`,
    { role },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );
  return response.data;
}

export async function updateAccessUserPlan(userId: string, plan: string) {
  const token = localStorage.getItem("access_token");
  const response = await api.patch(
    `/admin/users/${userId}/plan`,
    { plan },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );
  return response.data;
}

export async function resetAccessUserPassword(userId: string, newPassword: string) {
  const token = localStorage.getItem("access_token");
  const response = await api.post(
    `/admin/users/${userId}/reset-password`,
    { new_password: newPassword },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );
  return response.data;
}

export async function deleteAccessUser(userId: string) {
  const token = localStorage.getItem("access_token");
  const response = await api.delete(`/admin/users/${userId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return response.data;
}

export async function updateAccessUserOverrides(userId: string, permission_overrides: Record<string, boolean>) {
  const token = localStorage.getItem("access_token");
  const response = await api.patch(
    `/admin/users/${userId}/overrides`,
    { permission_overrides },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );
  return response.data;
}

export interface PermissionMatrixSettings {
  show_locked_tabs: boolean;
}

export interface PermissionMatrixResponse {
  matrix: Record<string, Record<string, boolean>>;
  settings?: PermissionMatrixSettings;
}

export async function fetchPermissionMatrix(): Promise<PermissionMatrixResponse> {
  const token = localStorage.getItem("access_token");
  const response = await api.get("/admin/permissions/matrix", {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return response.data;
}

export async function savePermissionMatrix(
  matrix: Record<string, Record<string, boolean>>,
  settings?: PermissionMatrixSettings
) {
  const token = localStorage.getItem("access_token");
  const response = await api.put(
    "/admin/permissions/matrix",
    { matrix, settings },
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
  );
  return response.data;
}

export async function fetchUserPermissions(): Promise<{
  permissions: Record<string, boolean>;
  show_locked_tabs: boolean;
  role: string;
}> {
  const token = localStorage.getItem("access_token");
  const response = await api.get("/auth/permissions", {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return response.data;
}
